import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/i18n";
import PageSeo from "@/components/PageSeo";

const BEFORE = "https://static.prod-images.emergentagent.com/jobs/36d97758-7bc2-4e44-8512-7d449bb86fe6/images/f0fe1217b4c5b455418608f6942b60828c3fdcbe0931c2a24be9a6460b8fb5b9.png";
const AFTER = "https://static.prod-images.emergentagent.com/jobs/36d97758-7bc2-4e44-8512-7d449bb86fe6/images/931717af37d60cfef15a0ff1ceaee739a26c6b51ad968f804ae952e5a0980153.png";

const GOLD = "#C7A668";
const SILVER = "#C9CED4";

export default function SampleReport() {
  const { L } = useLang();
  const nav = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const meta = [
    [L("Aircraft", "Aeronave"), "Citation CJ3"],
    [L("Registration", "Matrícula"), L("Hidden · Discretion Mode", "Oculta · Modo Discreción")],
    [L("Location", "Ubicación"), "Toluca · Universal Aviation FBO"],
    [L("Service", "Servicio"), "Full Detail"],
    [L("Specialist", "Especialista"), "A. M."],
    [L("Started", "Inicio"), "14:05"],
    [L("Completed", "Fin"), "18:21"],
    [L("Duration", "Duración"), "4h 16m"],
  ];

  const checklist = [
    L("Cabin vacuum & full surface wipe-down", "Aspirado de cabina y limpieza total de superficies"),
    L("Leather seats cleaned & conditioned", "Asientos de piel limpiados y acondicionados"),
    L("Windows & acrylics safe-cleaned", "Ventanas y acrílicos limpiados de forma segura"),
    L("Wood veneer polished", "Veneer de madera pulido"),
    L("Galley & lavatory reset", "Galley y lavatorio reset"),
    L("Carpet shampoo & spot treatment", "Shampoo de alfombra y tratamiento de manchas"),
    L("Seatbelts aligned & cabin staged", "Cinturones alineados y cabina presentada"),
    L("Neutral odor finish", "Acabado de olor neutro"),
  ];

  return (
    <div className="grain" style={{ minHeight: "100vh", paddingTop: 110, paddingBottom: 90 }}>
      <PageSeo esPath="/sample-report" enPath="/en/sample-report" title={L("Sample Aircraft Readiness Report | dukk", "Reporte de Readiness de Aeronave (Ejemplo) | dukk")} description={L("See an example dukk aircraft readiness report — documented cabin care, detailing checklist and before/after evidence for private aviation in Mexico.", "Mira un ejemplo de reporte de readiness de dukk — cabin care documentado, checklist de detailing y evidencia antes/después para aviación privada en México.")} />
      <div style={{ maxWidth: 940, margin: "0 auto", padding: "0 clamp(20px,5vw,40px)" }}>
        <button data-testid="report-back" onClick={() => nav(-1)} className="font-mono" style={{ fontSize: 11, letterSpacing: ".14em", textTransform: "uppercase", color: "#7d818a", background: "none", border: "none", cursor: "pointer", marginBottom: 26 }}>← {L("Back", "Atrás")}</button>

        <div className="glass" style={{ borderRadius: 8, overflow: "hidden" }}>
          {/* header */}
          <div style={{ padding: "clamp(24px,3vw,38px)", borderBottom: "1px solid rgba(255,255,255,.1)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 18 }}>
            <div>
              <div className="font-mono" style={{ fontSize: 10.5, letterSpacing: ".18em", color: GOLD, marginBottom: 12 }}>{L("AIRCRAFT READINESS REPORT", "REPORTE DE READINESS")}</div>
              <h1 style={{ fontSize: "clamp(26px,3.4vw,38px)", fontWeight: 400, margin: 0, letterSpacing: "-.015em" }}>{L("Departure-ready.", "Listo para salir.")}</h1>
              <div className="font-mono" style={{ fontSize: 13, color: "#9DA1A8", marginTop: 8 }}>#TB-1047 · Citation CJ3</div>
            </div>
            <span className="font-mono" style={{ fontSize: 11.5, letterSpacing: ".12em", color: "#0a0a0a", background: GOLD, borderRadius: 4, padding: "9px 16px", fontWeight: 700, whiteSpace: "nowrap" }}>{L("DEPARTURE-READY", "LISTO PARA SALIR")}</span>
          </div>

          {/* meta */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 1, background: "rgba(255,255,255,.06)" }}>
            {meta.map(([k, v], i) => (
              <div key={i} style={{ background: "#0b0c0e", padding: "16px 18px" }}>
                <div className="font-mono" style={{ fontSize: 9.5, letterSpacing: ".1em", color: "#7d818a", marginBottom: 6 }}>{k.toUpperCase()}</div>
                <div style={{ fontSize: 13.5, color: "#E7E4DD" }}>{v}</div>
              </div>
            ))}
          </div>

          {/* before / after */}
          <div style={{ padding: "clamp(24px,3vw,38px)" }}>
            <div className="font-mono" style={{ fontSize: 10.5, letterSpacing: ".14em", color: SILVER, marginBottom: 16 }}>{L("BEFORE / AFTER", "ANTES / DESPUÉS")}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 14 }}>
              {[[BEFORE, L("BEFORE", "ANTES"), "#9DA1A8"], [AFTER, L("AFTER", "DESPUÉS"), GOLD]].map(([src, label, c], i) => (
                <div key={i} style={{ position: "relative", borderRadius: 6, overflow: "hidden", border: "1px solid rgba(255,255,255,.1)" }}>
                  <img data-testid={`report-img-${i}`} src={src} alt={label} style={{ width: "100%", height: 240, objectFit: "cover", display: "block" }} />
                  <span className="font-mono" style={{ position: "absolute", top: 12, left: 12, fontSize: 10, letterSpacing: ".12em", color: i === 0 ? "#0a0a0a" : "#0a0a0a", background: c, borderRadius: 3, padding: "5px 10px", fontWeight: 700 }}>{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* checklist + observations */}
          <div style={{ padding: "0 clamp(24px,3vw,38px) clamp(24px,3vw,38px)", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: "clamp(20px,3vw,40px)" }}>
            <div>
              <div className="font-mono" style={{ fontSize: 10.5, letterSpacing: ".14em", color: SILVER, margin: "8px 0 16px" }}>{L("CHECKLIST COMPLETED", "CHECKLIST COMPLETADO")}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
                {checklist.map((c, i) => (
                  <div key={i} style={{ display: "flex", gap: 11, alignItems: "flex-start", fontSize: 14, color: "#d7dadd" }}><span style={{ color: GOLD, marginTop: 1 }}>✓</span>{c}</div>
                ))}
              </div>
            </div>
            <div>
              <div className="font-mono" style={{ fontSize: 10.5, letterSpacing: ".14em", color: SILVER, margin: "8px 0 16px" }}>{L("OBSERVATIONS", "OBSERVACIONES")}</div>
              <p style={{ fontSize: 14, color: "#B8BDC3", lineHeight: 1.65, margin: "0 0 18px" }}>
                {L("Light coffee staining on the forward right seat fully removed. Galley surfaces restored. Cabin staged for VIP boarding. No pre-existing damage observed beyond normal wear.", "Mancha ligera de café en el asiento delantero derecho removida por completo. Superficies del galley restauradas. Cabina presentada para abordaje VIP. Sin daños preexistentes más allá del desgaste normal.")}
              </p>
              <div className="font-mono" style={{ fontSize: 10.5, letterSpacing: ".14em", color: SILVER, margin: "0 0 12px" }}>{L("RECOMMENDATION", "RECOMENDACIÓN")}</div>
              <p style={{ fontSize: 14, color: "#B8BDC3", lineHeight: 1.65, margin: 0 }}>
                {L("Schedule a Cabin Ready reset before the next VIP departure to maintain presentation.", "Programar un Cabin Ready antes de la próxima salida VIP para mantener la presentación.")}
              </p>
            </div>
          </div>

          {/* footer status */}
          <div style={{ padding: "20px clamp(24px,3vw,38px)", borderTop: "1px solid rgba(255,255,255,.1)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span className="tb-pulse" style={{ width: 8, height: 8, borderRadius: "50%", background: SILVER }} />
              <span style={{ fontSize: 13.5, color: "#E7E4DD" }}>{L("Final status: Departure-ready", "Estatus final: Listo para salir")}</span>
            </div>
            <span className="font-mono" style={{ fontSize: 10.5, color: "#6c727a" }}>{L("Sample report — illustrative", "Reporte de ejemplo — ilustrativo")}</span>
          </div>
        </div>

        <p className="font-mono" style={{ marginTop: 26, fontSize: 11, color: "#5b616a", textAlign: "center" }}>// Silent service. Visible quality. Departure-ready. · DUKK</p>
      </div>
    </div>
  );
}
